/**
 * Process-local progress registry for voice-note preparation.
 *
 * A voice-note upload/transcription deliberately outlives its originating
 * useAgnoChat instance when the user changes sessions. React state from that
 * unmounted instance is no longer observable, so this small external store
 * keeps progress scoped to the session until the pre-chat work completes.
 */

export type VoiceNoteProgressPhase = "uploading" | "transcribing";

export interface VoiceNoteProgressHandle {
  setPhase: (phase: VoiceNoteProgressPhase) => void;
  complete: () => void;
}

type ProgressEntries = Map<number, VoiceNoteProgressPhase>;

const entriesBySession = new Map<string, ProgressEntries>();
const listeners = new Set<() => void>();
let nextEntryId = 0;
let revision = 0;

function notify(): void {
  revision += 1;
  for (const listener of listeners) listener();
}

/** Begin tracking one voice note's upload/transcription lifecycle. */
export function beginVoiceNoteProgress(sessionId: string): VoiceNoteProgressHandle {
  const entryId = nextEntryId++;
  const entries = entriesBySession.get(sessionId) ?? new Map<number, VoiceNoteProgressPhase>();
  entries.set(entryId, "uploading");
  entriesBySession.set(sessionId, entries);
  notify();

  let completed = false;

  return {
    setPhase(phase) {
      if (completed) return;
      const current = entriesBySession.get(sessionId)?.get(entryId);
      if (!current || current === phase) return;
      entriesBySession.get(sessionId)?.set(entryId, phase);
      notify();
    },
    complete() {
      if (completed) return;
      completed = true;

      const currentEntries = entriesBySession.get(sessionId);
      if (!currentEntries?.delete(entryId)) return;
      if (currentEntries.size === 0) entriesBySession.delete(sessionId);
      notify();
    },
  };
}

/** Return the visible phase for a session, if any voice note is still pending. */
export function getVoiceNoteProgress(sessionId: string): VoiceNoteProgressPhase | null {
  const entries = entriesBySession.get(sessionId);
  if (!entries || entries.size === 0) return null;

  // Transcription is the later and more specific stage, so prefer it if
  // overlapping sends ever leave both stages active for one session.
  for (const phase of entries.values()) {
    if (phase === "transcribing") return "transcribing";
  }
  return "uploading";
}

/** Snapshot the sessions with any voice-note preparation work in progress. */
export function getVoiceNoteProgressSessionIds(): ReadonlySet<string> {
  return new Set(entriesBySession.keys());
}

/** Subscribe UI consumers to progress changes. */
export function subscribeToVoiceNoteProgress(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Stable primitive snapshot for useSyncExternalStore consumers. */
export function getVoiceNoteProgressRevision(): number {
  return revision;
}

/** @internal Exported for isolated module-store tests. */
export function __resetVoiceNoteProgressForTests(): void {
  entriesBySession.clear();
  nextEntryId = 0;
  notify();
}
